import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
  Input,
  SectionCard,
  FormSection,
  Switch,
} from '@/components/ui';
import type { FaqCategory, FaqCategoryPayload } from '../types/faq';

interface FaqCategoryFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  editTarget?: FaqCategory | null;
  isPending: boolean;
  onSubmit: (payload: FaqCategoryPayload) => void;
}

export const FaqCategoryFormModal = ({
  open,
  onOpenChange,
  mode,
  editTarget,
  isPending,
  onSubmit,
}: FaqCategoryFormModalProps) => {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && editTarget) {
      setName(editTarget.name);
      setSortOrder(String(editTarget.sortOrder));
      setIsActive(editTarget.isActive);
    } else {
      setName('');
      setSortOrder('0');
      setIsActive(true);
    }
  }, [open, mode, editTarget]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      sortOrder: Number(sortOrder) || 0,
      isActive,
    } satisfies FaqCategoryPayload);
  };

  const titleText = mode === 'create' ? t('faq.categories.createTitle') : t('faq.categories.editTitle');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titleText}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <SectionCard>
            <div className="flex flex-col gap-3">
              <FormSection density="compact" label={t('faq.categories.fields.name')} required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  maxLength={100}
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </FormSection>
              <FormSection density="compact" label={t('faq.categories.fields.sortOrder')}>
                <Input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  min={0}
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </FormSection>
              <FormSection density="compact" label={t('faq.categories.fields.isActive')}>
                <Switch
                  checked={isActive}
                  onCheckedChange={setIsActive}
                  aria-label={t('faq.categories.fields.isActive')}
                />
              </FormSection>
            </div>
          </SectionCard>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">{t('faq.cancel')}</Button>
            </DialogClose>
            <Button type="submit" size="sm" disabled={isPending || !name.trim()}>
              {t('faq.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
